export const SERVICE_KEYS = [
  'blackfriday',
  'epilation',
  'packminceurcomplet',
  'teeth',
  'hydrafacial',
  'microneedling',
  'hifu',
  'powershape',
  'pressotherapie',
  'carbonpeel',
] as const;

export type ServiceKey = (typeof SERVICE_KEYS)[number];

export const SERVICE_PRICING_OPTIONS: Partial<Record<ServiceKey, readonly { value: string; label: string }[]>> = {
  blackfriday: [
    { value: 'laser_diode', label: 'Laser Diode 149 dh/zone' },
    { value: 'lumiere_pulsee', label: 'Lumière pulsée 99 dh/zone' },
    { value: 'laser_alexandrite', label: 'Laser Alexandrite 249 dh/zone' },
    { value: 'black_pack_minceur', label: 'Black Pack Minceur 999 dh' },
  ],
  epilation: [
  { value: 'per_zone_session', label: '300 DH par zone / séance' },
  { value: 'six_sessions', label: '6 séances à 1500 DH / zone' },
  ],
  packminceurcomplet: [
    { value: 'basic', label: 'Pack Basic : 3500 DH' },
    { value: 'complete', label: 'Pack Complet : 5000 DH' },
  ],
  teeth: [{ value: 'per_session', label: '1500 DH la séance' }],
  hydrafacial: [{ value: 'per_session', label: '500 DH la séance' }],
  microneedling: [{ value: 'per_session', label: '1000 DH la séance' }],
  hifu: [{ value: 'per_session', label: '1300 DH la séance' }],
  powershape: [
    { value: 'per_session', label: '350 DH la séance' },
    { value: 'ten_sessions', label: '10 séances à 3000 DH' },
  ],
  pressotherapie: [
    { value: 'per_session', label: '300 DH la séance' },
    { value: 'ten_sessions', label: '10 séances à 2000 DH' },
  ],
  carbonpeel: [{ value: 'per_session', label: '700 DH la séance' }],
};

export const SERVICE_PARAM_ALIASES: Record<string, ServiceKey> = {
  blackfriday: 'blackfriday',
  'black-friday': 'blackfriday',
  'offres-black-friday': 'blackfriday',
  epilation: 'epilation',
  'epilation-laser': 'epilation',
  'epilation-laser-300': 'epilation',
  packminceurcomplet: 'packminceurcomplet',
  'pack-minceur-complet': 'packminceurcomplet',
  teeth: 'teeth',
  blanchimentdentaire: 'teeth',
  'blanchiment-dentaire': 'teeth',
  hydrafacial: 'hydrafacial',
  'hydrafacial-complet': 'hydrafacial',
  'hydra-facial': 'hydrafacial',
  microneedling: 'microneedling',
  'micro-needling': 'microneedling',
  hifu: 'hifu',
  'hi-fu': 'hifu',
  powershape: 'powershape',
  'power-shape': 'powershape',
  pressotherapie: 'pressotherapie',
  'pressothérapie': 'pressotherapie',
  carbonpeel: 'carbonpeel',
  'carbon-peel': 'carbonpeel',
  'carbon peel': 'carbonpeel',
  bodycare: 'hydrafacial',
  'body-care': 'hydrafacial',
  weightloss: 'packminceurcomplet',
  'weight-loss': 'packminceurcomplet',
  skincare: 'hydrafacial',
  'skin-care': 'hydrafacial',
  packhydrafacialcarbonpeelteeth: 'hydrafacial',
  'pack-hydrafacial-carbon-peel-teeth': 'hydrafacial',
  'pack-hydrafacial-carbon-peel-teeth-whitening': 'hydrafacial',
  'pack-hydrafacial-carbon-peel-blanchiment-dentaire': 'hydrafacial',
  'hydrafacial-carbon-peel-blanchiment-dentaire': 'hydrafacial',
  'pack-hydrafacial-&-carbon-peel-+-blanchiment-dentaire': 'hydrafacial',
  'pack-hydrafacial-&-carbon-peel-blanchiment-dentaire': 'hydrafacial',
  'باك-الهيدرافاشيال-والكربون-بيل-وتبييض-الأسنان': 'hydrafacial',
  'hydrafacial-carbon-peel-teeth-whitening': 'hydrafacial',
};

const SERVICE_LABELS: Record<'fr' | 'ar' | 'en', Record<ServiceKey, string>> = {
  fr: {
    blackfriday: 'OFFRES BLACK FRIDAY (TICKETS)',
    epilation: 'ÉPILATION LASER',
    packminceurcomplet: 'PROGRAMME MINCEUR',
    teeth: 'BLANCHIMENT DENTAIRE',
    hydrafacial: 'HYDRAFACIAL COMPLET',
    microneedling: 'MICRONEEDLING',
    hifu: 'HIFU VISAGE',
    powershape: 'POWER SHAPE / LIPOCAVITATION',
    pressotherapie: 'PRESSOTHÉRAPIE',
    carbonpeel: 'CARBON PEEL',
  },
  ar: {
    blackfriday: 'عروض الجمعة السوداء (تذاكر)',
    epilation: 'إزالة الشعر بالليزر',
    packminceurcomplet: 'برنامج العناية بالقوام',
    teeth: 'تبييض الأسنان',
    hydrafacial: 'هيدرافيشل كامل',
    microneedling: 'ميكرونيدلينغ',
    hifu: 'HIFU للوجه',
    powershape: 'باور شيب / ليبوكافيتيشن',
    pressotherapie: 'بريسوثيرابي',
    carbonpeel: 'كاربون بيل',
  },
  en: {
    blackfriday: 'BLACK FRIDAY OFFERS (TICKETS)',
    epilation: 'LASER HAIR REMOVAL',
    packminceurcomplet: 'BODY CONTOURING PROGRAMME',
    teeth: 'TEETH WHITENING',
    hydrafacial: 'COMPLETE HYDRAFACIAL',
    microneedling: 'MICRONEEDLING',
    hifu: 'FACIAL HIFU',
    powershape: 'POWER SHAPE / LIPOCAVITATION',
    pressotherapie: 'PRESSOTHERAPY',
    carbonpeel: 'CARBON PEEL',
  },
};

function normalizeLanguage(language: string): 'fr' | 'ar' | 'en' {
  if (language === 'fr' || language === 'ar' || language === 'en') {
    return language;
  }
  return 'en';
}

export function getServiceLabel(service: string, language: string): string {
  const normalizedLanguage = normalizeLanguage(language);
  const labels = SERVICE_LABELS[normalizedLanguage];
  return labels[service as ServiceKey] || service;
}
